import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";

type Status = "loading" | "valid" | "already_unsubscribed" | "invalid" | "success" | "error";

export default function Unsubscribe() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [status, setStatus] = useState<Status>("loading");

  useEffect(() => {
    if (!token) {
      setStatus("invalid");
      return;
    }

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

    fetch(`${supabaseUrl}/functions/v1/handle-email-unsubscribe?token=${token}`, {
      headers: { apikey: anonKey },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.valid === false && data.reason === "already_unsubscribed") {
          setStatus("already_unsubscribed");
        } else if (data.valid) {
          setStatus("valid");
        } else {
          setStatus("invalid");
        }
      })
      .catch(() => setStatus("invalid"));
  }, [token]);

  const handleUnsubscribe = async () => {
    if (!token) return;
    try {
      const { data, error } = await supabase.functions.invoke("handle-email-unsubscribe", {
        body: { token },
      });
      if (error) throw error;
      if (data?.success) {
        setStatus("success");
      } else if (data?.reason === "already_unsubscribed") {
        setStatus("already_unsubscribed");
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm space-y-6 text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">supportfyi</h1>

        {status === "loading" && (
          <p className="text-sm text-muted-foreground">Validating…</p>
        )}

        {status === "valid" && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Are you sure you want to unsubscribe from email notifications?
            </p>
            <Button onClick={handleUnsubscribe} className="w-full">
              Confirm Unsubscribe
            </Button>
          </div>
        )}

        {status === "success" && (
          <p className="text-sm text-muted-foreground">
            You've been unsubscribed. You will no longer receive these emails.
          </p>
        )}

        {status === "already_unsubscribed" && (
          <p className="text-sm text-muted-foreground">
            You're already unsubscribed from these emails.
          </p>
        )}

        {status === "invalid" && (
          <p className="text-sm text-muted-foreground">
            This unsubscribe link is invalid or has expired.
          </p>
        )}

        {status === "error" && (
          <p className="text-sm text-destructive">
            Something went wrong. Please try again later.
          </p>
        )}
      </div>
    </div>
  );
}
